import { defineStore } from 'pinia'
import { ref, computed } from 'vue'

// 消息类型枚举
export enum MessageType {
  INTERACTION = 'interaction',  // 互动消息（点赞、守护、评论）
  SYSTEM = 'system',            // 系统消息
  NOTICE = 'notice'             // 通知
}

// 消息接口
interface Message {
  id: string
  type: MessageType
  title: string
  content: string
  isRead: boolean
  createTime: string
  relatedId?: string
  relatedType?: string
  sender?: {
    id: string
    nickname: string
    avatar: string
  }
}

export const useMessageStore = defineStore('message', () => {
  // State
  const messages = ref<Message[]>([])
  const unreadCount = ref(0)
  const unreadInteractionCount = ref(0)
  const unreadSystemCount = ref(0)
  const loading = ref(false)
  const hasMore = ref(true)

  // Getters
  const totalUnread = computed(() => {
    return unreadInteractionCount.value + unreadSystemCount.value
  })

  const interactionMessages = computed(() => {
    return messages.value.filter(m => m.type === MessageType.INTERACTION)
  })

  const systemMessages = computed(() => {
    return messages.value.filter(m => m.type === MessageType.SYSTEM)
  })

  // Actions
  const fetchUnreadCount = async () => {
    try {
      const token = uni.getStorageSync('token') as string
      if (!token) return
      
      const res = await uni.request({
        url: 'https://your-api-domain.com/api/message/unread',
        method: 'GET',
        header: {
          'Authorization': `Bearer ${token}`
        }
      })
      
      const data = res.data as any
      if (data.code === 200) {
        unreadCount.value = data.data.total || 0
        unreadInteractionCount.value = data.data.interaction || 0
        unreadSystemCount.value = data.data.system || 0
      }
    } catch (e) {
      console.error('获取未读消息数失败:', e)
    }
  }

  const fetchMessages = async (type?: MessageType, page = 1, size = 20) => {
    if (loading.value) return
    if (page === 1) {
      messages.value = []
      hasMore.value = true
    }
    
    loading.value = true
    try {
      const token = uni.getStorageSync('token') as string
      const res = await uni.request({
        url: 'https://your-api-domain.com/api/message/list',
        method: 'GET',
        header: {
          'Authorization': `Bearer ${token}`
        },
        data: { type, page, size }
      })
      
      const data = res.data as any
      if (data.code === 200) {
        const list = data.data.list || []
        if (page === 1) {
          messages.value = list
        } else {
          messages.value.push(...list)
        }
        hasMore.value = list.length === size
        
        // 更新未读数
        fetchUnreadCount()
      }
    } catch (e) {
      console.error('获取消息列表失败:', e)
    } finally {
      loading.value = false
    }
  }

  const markAsRead = async (id: string) => {
    try {
      const token = uni.getStorageSync('token') as string
      await uni.request({
        url: `https://your-api-domain.com/api/message/${id}/read`,
        method: 'PUT',
        header: {
          'Authorization': `Bearer ${token}`
        }
      })
      
      // 更新本地状态
      const msg = messages.value.find(m => m.id === id)
      if (msg && !msg.isRead) {
        msg.isRead = true
        if (msg.type === MessageType.INTERACTION) {
          unreadInteractionCount.value = Math.max(0, unreadInteractionCount.value - 1)
        } else if (msg.type === MessageType.SYSTEM) {
          unreadSystemCount.value = Math.max(0, unreadSystemCount.value - 1)
        }
        unreadCount.value = Math.max(0, unreadCount.value - 1)
      }
    } catch (e) {
      console.error('标记已读失败:', e)
    }
  }

  const markAllAsRead = async (type?: MessageType) => {
    try {
      const token = uni.getStorageSync('token') as string
      await uni.request({
        url: 'https://your-api-domain.com/api/message/read-all',
        method: 'PUT',
        header: {
          'Authorization': `Bearer ${token}`
        },
        data: { type }
      })
      
      // 更新本地状态
      messages.value.forEach(msg => {
        if (!type || msg.type === type) {
          msg.isRead = true
        }
      })
      
      if (!type || type === MessageType.INTERACTION) {
        unreadInteractionCount.value = 0
      }
      if (!type || type === MessageType.SYSTEM) {
        unreadSystemCount.value = 0
      }
      unreadCount.value = unreadInteractionCount.value + unreadSystemCount.value
      
      uni.showToast({ title: '已全部已读', icon: 'success' })
    } catch (e) {
      console.error('标记全部已读失败:', e)
    }
  }

  const deleteMessage = async (id: string) => {
    try {
      const token = uni.getStorageSync('token') as string
      await uni.request({
        url: `https://your-api-domain.com/api/message/${id}`,
        method: 'DELETE',
        header: {
          'Authorization': `Bearer ${token}`
        }
      })
      
      // 从本地列表移除
      const index = messages.value.findIndex(m => m.id === id)
      if (index > -1) {
        const msg = messages.value[index]
        if (!msg.isRead) {
          if (msg.type === MessageType.INTERACTION) {
            unreadInteractionCount.value = Math.max(0, unreadInteractionCount.value - 1)
          } else if (msg.type === MessageType.SYSTEM) {
            unreadSystemCount.value = Math.max(0, unreadSystemCount.value - 1)
          }
          unreadCount.value = Math.max(0, unreadCount.value - 1)
        }
        messages.value.splice(index, 1)
      }
      
      uni.showToast({ title: '删除成功', icon: 'success' })
    } catch (e) {
      console.error('删除消息失败:', e)
      uni.showToast({ title: '删除失败', icon: 'none' })
    }
  }

  const addMessage = (message: Message) => {
    messages.value.unshift(message)
    if (!message.isRead) {
      unreadCount.value++
      if (message.type === MessageType.INTERACTION) {
        unreadInteractionCount.value++
      } else if (message.type === MessageType.SYSTEM) {
        unreadSystemCount.value++
      }
    }
  }

  return {
    messages,
    unreadCount,
    unreadInteractionCount,
    unreadSystemCount,
    loading,
    hasMore,
    totalUnread,
    interactionMessages,
    systemMessages,
    fetchUnreadCount,
    fetchMessages,
    markAsRead,
    markAllAsRead,
    deleteMessage,
    addMessage
  }
})
